/**
 * Feature Flag Service
 * 
 * Central service for managing feature flags across the entire application.
 * Supports runtime toggling, whitelisting, and event-based updates.
 * 
 * Usage:
 *   import { featureFlags } from '@/core/features/FeatureFlagService';
 *   
 *   // Check if feature is visible
 *   if (featureFlags.isVisible('devtools', 'healButton')) { ... }
 *   
 *   // Enable whitelist mode for testing
 *   featureFlags.setMode('devtools', FeatureMode.WHITELIST);
 *   featureFlags.whitelist('devtools', 'healButton');
 *   
 *   // Listen for changes
 *   featureFlags.onFeatureChange((e) => console.log(e));
 */

import {
  FeatureMode,
  FeatureSetConfig,
  FeatureRegistry,
  FeatureChangeEvent,
  DomainModeChangeEvent,
  WhitelistChangeEvent,
  FeatureChangeListener,
  DomainModeChangeListener,
  WhitelistChangeListener,
  CheckFeatureOptions,
  FeatureCheckResult,
  FeatureConfig,
} from './FeatureTypes';

import { gameLogger as logger } from '../../utils/GameLogger';

class FeatureFlagService {
  private registry: FeatureRegistry = {};
  private featureListeners: Set<FeatureChangeListener> = new Set();
  private modeListeners: Set<DomainModeChangeListener> = new Set();
  private whitelistListeners: Set<WhitelistChangeListener> = new Set();
  private initialized = false;

  // =============================================================================
  // INITIALIZATION
  // =============================================================================

  /**
   * Initialize the service with feature configurations
   */
  init(config: FeatureRegistry): void {
    if (this.initialized) {
      logger.warn('features', 'FeatureFlagService already initialized, overwriting config');
    }

    this.registry = { ...config };
    this.initialized = true;

    logger.info('features', 'FeatureFlagService initialized', {
      domains: Object.keys(config),
      domainCount: Object.keys(config).length,
    });

    // Expose to window for console debugging
    if (typeof window !== 'undefined') {
      (window as any).featureFlags = this;
      (window as any).FeatureMode = FeatureMode;
      this.printConsoleHelp();
    }
  }

  /**
   * Register a new feature domain dynamically
   */
  registerDomain(domain: string, config: FeatureSetConfig): void {
    this.registry[domain] = { ...config };
    logger.info('features', `Registered domain '${domain}'`, {
      featureCount: Object.keys(config.features).length,
    });
  }

  // =============================================================================
  // FEATURE CHECKS
  // =============================================================================

  /**
   * Check if a feature should be visible
   */
  isVisible(domain: string, feature: string, _options: CheckFeatureOptions = {}): boolean {
    const result = this.check(domain, feature);
    return result.visible;
  }

  /**
   * Check feature visibility with detailed result
   */
  check(domain: string, feature: string): FeatureCheckResult {
    const domainConfig = this.registry[domain];

    // Domain not found
    if (!domainConfig) {
      return {
        visible: false,
        reason: 'not-found',
        mode: FeatureMode.DISABLED,
        whitelisted: false,
      };
    }

    // Domain is in disabled mode
    if (domainConfig.mode === FeatureMode.DISABLED) {
      return {
        visible: false,
        reason: 'mode-disabled',
        mode: domainConfig.mode,
        whitelisted: false,
      };
    }

    // Whitelist mode - only whitelisted features visible
    if (domainConfig.mode === FeatureMode.WHITELIST) {
      const isWhitelisted = domainConfig.whitelist.includes(feature);
      return {
        visible: isWhitelisted,
        reason: isWhitelisted ? 'whitelisted' : 'disabled',
        mode: domainConfig.mode,
        whitelisted: isWhitelisted,
      };
    }

    // Normal mode - check if feature exists and is enabled
    const featureConfig = domainConfig.features[feature];
    if (!featureConfig) {
      return {
        visible: false,
        reason: 'not-found',
        mode: domainConfig.mode,
        whitelisted: false,
      };
    }

    return {
      visible: featureConfig.enabled,
      reason: featureConfig.enabled ? 'enabled' : 'disabled',
      mode: domainConfig.mode,
      whitelisted: false,
    };
  }

  // =============================================================================
  // GETTERS
  // =============================================================================

  /**
   * Get full configuration for a domain
   */
  getDomainConfig(domain: string): FeatureSetConfig | undefined {
    return this.registry[domain];
  }

  /**
   * Get configuration for a specific feature
   */
  getFeatureConfig(domain: string, feature: string): FeatureConfig | undefined {
    return this.registry[domain]?.features[feature];
  }

  /**
   * Get current mode for a domain
   */
  getMode(domain: string): FeatureMode {
    return this.registry[domain]?.mode ?? FeatureMode.NORMAL;
  }

  /**
   * Get current whitelist for a domain
   */
  getWhitelist(domain: string): string[] {
    return [...(this.registry[domain]?.whitelist ?? [])];
  }

  /**
   * Get all enabled features for a domain (respects mode)
   */
  getEnabledFeatures(domain: string): string[] {
    const config = this.registry[domain];
    if (!config) return [];

    if (config.mode === FeatureMode.WHITELIST) {
      return config.whitelist;
    }

    if (config.mode === FeatureMode.DISABLED) {
      return [];
    }

    return Object.entries(config.features)
      .filter(([_, cfg]) => cfg.enabled)
      .map(([name]) => name);
  }

  /**
   * Get all available features for a domain (ignores mode)
   */
  getAllFeatures(domain: string): string[] {
    return Object.keys(this.registry[domain]?.features ?? {});
  }

  /**
   * Get all registered domains
   */
  getDomains(): string[] {
    return Object.keys(this.registry);
  }

  // =============================================================================
  // SETTERS (with events)
  // =============================================================================

  /**
   * Enable/disable a specific feature
   */
  setEnabled(domain: string, feature: string, enabled: boolean): boolean {
    const domainConfig = this.registry[domain];
    if (!domainConfig) {
      logger.warn('features', `Cannot set feature: domain '${domain}' not found`);
      return false;
    }

    const featureConfig = domainConfig.features[feature];
    if (!featureConfig) {
      logger.warn('features', `Cannot set feature: '${feature}' not found in domain '${domain}'`);
      return false;
    }

    const previousValue = featureConfig.enabled;
    featureConfig.enabled = enabled;

    const event: FeatureChangeEvent = {
      domain,
      feature,
      previousValue,
      newValue: enabled,
      timestamp: Date.now(),
    };

    this.featureListeners.forEach(listener => {
      try {
        listener(event);
      } catch (e) {
        logger.error('features', 'Error in feature change listener', e);
      }
    });

    logger.info('features', `Feature '${domain}.${feature}' ${enabled ? 'enabled' : 'disabled'}`, event);
    return true;
  }

  /**
   * Toggle a feature on/off
   */
  toggle(domain: string, feature: string): boolean {
    const current = this.registry[domain]?.features[feature]?.enabled ?? false;
    this.setEnabled(domain, feature, !current);
    return !current;
  }

  /**
   * Set the mode for a domain
   */
  setMode(domain: string, mode: FeatureMode): boolean {
    const domainConfig = this.registry[domain];
    if (!domainConfig) {
      logger.warn('features', `Cannot set mode: domain '${domain}' not found`);
      return false;
    }

    const previousMode = domainConfig.mode;
    domainConfig.mode = mode;

    const event: DomainModeChangeEvent = {
      domain,
      previousMode,
      newMode: mode,
      timestamp: Date.now(),
    };

    this.modeListeners.forEach(listener => {
      try {
        listener(event);
      } catch (e) {
        logger.error('features', 'Error in mode change listener', e);
      }
    });

    logger.info('features', `Domain '${domain}' mode changed to ${mode}`, event);
    return true;
  }

  // =============================================================================
  // WHITELIST MANAGEMENT
  // =============================================================================

  /**
   * Add feature(s) to whitelist
   */
  whitelist(domain: string, ...features: string[]): boolean {
    const domainConfig = this.registry[domain];
    if (!domainConfig) {
      logger.warn('features', `Cannot whitelist: domain '${domain}' not found`);
      return false;
    }

    const previousWhitelist = [...domainConfig.whitelist];
    
    features.forEach(feature => {
      if (!domainConfig.whitelist.includes(feature)) {
        domainConfig.whitelist.push(feature);
      }
    });

    const event: WhitelistChangeEvent = {
      domain,
      previousWhitelist,
      newWhitelist: [...domainConfig.whitelist],
      timestamp: Date.now(),
    };

    this.whitelistListeners.forEach(listener => {
      try {
        listener(event);
      } catch (e) {
        logger.error('features', 'Error in whitelist change listener', e);
      }
    });

    logger.info('features', `Whitelisted ${features.length} feature(s) in '${domain}'`, {
      domain,
      features,
      whitelist: domainConfig.whitelist,
    });

    return true;
  }

  /**
   * Remove feature(s) from whitelist
   */
  unwhitelist(domain: string, ...features: string[]): boolean {
    const domainConfig = this.registry[domain];
    if (!domainConfig) {
      logger.warn('features', `Cannot unwhitelist: domain '${domain}' not found`);
      return false;
    }

    const previousWhitelist = [...domainConfig.whitelist];
    domainConfig.whitelist = domainConfig.whitelist.filter(f => !features.includes(f));

    const event: WhitelistChangeEvent = {
      domain,
      previousWhitelist,
      newWhitelist: [...domainConfig.whitelist],
      timestamp: Date.now(),
    };

    this.whitelistListeners.forEach(listener => {
      try {
        listener(event);
      } catch (e) {
        logger.error('features', 'Error in whitelist change listener', e);
      }
    });

    logger.info('features', `Removed ${features.length} feature(s) from whitelist in '${domain}'`, {
      domain,
      features,
      whitelist: domainConfig.whitelist,
    });

    return true;
  }

  /**
   * Set the entire whitelist (replaces existing)
   */
  setWhitelist(domain: string, features: string[]): boolean {
    const domainConfig = this.registry[domain];
    if (!domainConfig) {
      logger.warn('features', `Cannot set whitelist: domain '${domain}' not found`);
      return false;
    }

    const previousWhitelist = [...domainConfig.whitelist];
    domainConfig.whitelist = [...features];

    const event: WhitelistChangeEvent = {
      domain,
      previousWhitelist,
      newWhitelist: [...features],
      timestamp: Date.now(),
    };

    this.whitelistListeners.forEach(listener => {
      try {
        listener(event);
      } catch (e) {
        logger.error('features', 'Error in whitelist change listener', e);
      }
    });

    logger.info('features', `Set whitelist for '${domain}'`, {
      domain,
      features,
    });

    return true;
  }

  /**
   * Clear the whitelist
   */
  clearWhitelist(domain: string): boolean {
    return this.setWhitelist(domain, []);
  }

  // =============================================================================
  // EVENT LISTENERS
  // =============================================================================

  onFeatureChange(listener: FeatureChangeListener): () => void {
    this.featureListeners.add(listener);
    return () => this.featureListeners.delete(listener);
  }

  onModeChange(listener: DomainModeChangeListener): () => void {
    this.modeListeners.add(listener);
    return () => this.modeListeners.delete(listener);
  }

  onWhitelistChange(listener: WhitelistChangeListener): () => void {
    this.whitelistListeners.add(listener);
    return () => this.whitelistListeners.delete(listener);
  }

  // =============================================================================
  // CONSOLE DEBUGGING
  // =============================================================================

  private printConsoleHelp(): void {
    console.log('%c[Feature Flags] Service ready!', 'color: #2ecc71; font-weight: bold;');
    console.log('Available commands:');
    console.log('  featureFlags.isVisible("devtools", "healButton") - Check if feature visible');
    console.log('  featureFlags.toggle("devtools", "healButton") - Toggle feature');
    console.log('  featureFlags.setMode("devtools", FeatureMode.WHITELIST) - Set mode');
    console.log('  featureFlags.whitelist("devtools", "healButton", "manaButton") - Add to whitelist');
    console.log('  featureFlags.unwhitelist("devtools", "healButton") - Remove from whitelist');
    console.log('  featureFlags.setWhitelist("devtools", ["healButton"]) - Set entire whitelist');
    console.log('  featureFlags.clearWhitelist("devtools") - Clear whitelist');
    console.log('  featureFlags.getEnabledFeatures("devtools") - List enabled features');
    console.log('  featureFlags.getDomains() - List all domains');
  }

  /**
   * Print current state to console (for debugging)
   */
  debug(): void {
    console.group('%c[Feature Flags] Current State', 'color: #3498db; font-weight: bold;');
    
    Object.entries(this.registry).forEach(([domain, config]) => {
      console.group(`%c${domain}%c [mode: ${config.mode}]`, 'color: #e74c3c; font-weight: bold;', 'color: #7f8c8d;');
      
      if (config.mode === FeatureMode.WHITELIST) {
        console.log('Whitelist:', config.whitelist.length ? config.whitelist.join(', ') : '(empty)');
      }
      
      console.log('Features:');
      Object.entries(config.features).forEach(([name, _cfg]) => {
        const visible = this.isVisible(domain, name);
        const color = visible ? '#2ecc71' : '#e74c3c';
        console.log(`  %c${visible ? '✓' : '✗'}%c ${name}`, `color: ${color}`, 'color: inherit');
      });
      
      console.groupEnd();
    });
    
    console.groupEnd();
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

export const featureFlags = new FeatureFlagService();
